import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo } from 'react'
import { Switch, useHistory } from 'react-router-dom'
import { AppAction, AppContext, IBackgroundLocationState } from '../config/appConfig'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../reducers'
import { useMount } from '@fluentui/react-hooks'
import { isAppContext } from '../helpers/appHelper'
import { Action, Location } from 'history'
import { Route } from 'react-router'
import Dashboard from './Dashboard'
import Files from './Files'
import Emails from './Emails'
import Parties from './Parties'
import Tasks from './Tasks'
import Times from './Times'
import Notes from './Notes'
import Finance from './Finance'
import Settings from './Settings'
import MessageContext from '../context/MessageContext'
import FileContext from '../context/FileContext'
import { extractReferenceNumbers } from '../helpers/referenceNumberHelper'
import useAxios from '../hooks/useAxios'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import { I18nAction } from '../config/i18nConfig'
import { useMollie } from '../hooks/useMollie'
import { useSettings } from '../hooks/useSettings'

const getPathFromHash = (): string => window.location.hash.substring(1)
const hashIsCorrect = (location: Location): boolean => getPathFromHash() === `#${location.pathname}`

export default function Router(): ReactElement {
    const settings = useSettings()
    const axios = useAxios()
    const { outlookMessage } = useContext(MessageContext)
    const { validateReferences } = useContext(FileContext)
    const history = useHistory<IBackgroundLocationState>()
    const dispatch = useDispatch()
    const currentPage = useSelector<Store, string>(state => state.page)
    const modules = useModules()
    const mollie = useMollie()
    const showDashboard = useMemo(() => settings?.dashboard?.enabled !== false, [settings])

    useMount(() => {
        if (!isAppContext([AppContext.Read, AppContext.Compose]) && currentPage) {
            history.push(currentPage)
        }
    })

    useMount(() => {
        mollie.authorize(window.location)
    })

    useEffect(() => {
        if (isAppContext(AppContext.Compose) && outlookMessage) {
            ;(outlookMessage as Office.MessageCompose).subject?.getAsync(subject =>
                validateReferences(extractReferenceNumbers(subject.value)).then(fileIdMap => {
                    if (fileIdMap.size === 1) {
                        const [fileId] = fileIdMap.values()
                        history.push(`/file/${fileId}`)
                    }
                }),
            )
        }
    }, [outlookMessage])

    useEffect(() => {
        const listener = () => {
            // Use the history object, because the useLocation() object is not yet updated when this method is called.
            // This causes issues with preserving the state when showing command dialogs.
            if (!hashIsCorrect(history.location)) {
                history.replace(getPathFromHash(), history.location.state)
            }
        }

        // Make sure that the path in memory gets updated if the hash changes by an external event.
        window.addEventListener('hashchange', listener)

        return () => window.removeEventListener('hashchange', listener)
    }, [])

    useEffect(
        () =>
            history.listen((location: Location, action: Action) => {
                if (['PUSH', 'REPLACE'].includes(action)) {
                    // We only want to keep track if we're not in a mailbox item context (i.e. fullscreen).
                    if (!isAppContext([AppContext.Read, AppContext.Compose])) {
                        // Set a new current page.
                        dispatch({
                            type: AppAction.SetCurrentPage,
                            payload: location.pathname,
                        })
                    }
                }

                if (['PUSH'].includes(action)) {
                    axios.post('/history/navigate', location.pathname, {
                        headers: { 'Content-Type': 'text/plain', SuppressErrors: true },
                    })
                }
            }),
        [],
    )

    useEffect(() => {
        dispatch({
            type: I18nAction.GetCountries,
            payload: axios.get('/country/list'),
        })
        dispatch({
            type: I18nAction.GetCurrencies,
            payload: axios.get('/currency/list'),
        })
    }, [])

    // Make sure that the hash location gets updated every time the page changes.
    window.location.hash = `#${history.location.pathname}`

    if (settings?.dashboard?.enabled === false && history?.location?.pathname === '/') {
        history.push('/file')
    }

    return (
        <Switch location={history.location.state?.background || history.location}>
            {showDashboard && <Route exact path="/" component={Dashboard} />}
            <Route path="/file" component={Files} />
            <Route path="/email" component={Emails} />
            <Route path="/party" component={Parties} />
            <Route path="/task" component={Tasks} />
            {modules.has(ModuleType.TimeTracking) && <Route path="/time" component={Times} />}
            <Route path="/note" component={Notes} />
            {modules.any([ModuleType.Invoices, ModuleType.Reports]) && <Route path="/finance" component={Finance} />}
            <Route path="/settings" component={Settings} />
        </Switch>
    )
}
